/**
 * The vocabulary probe that sets a learner's starting level.
 *
 * This is a Yes/No vocabulary test (Meara): the learner is shown words and says which
 * they know, which is far faster than multiple choice and correlates well with active
 * vocabulary size. Its known weakness is over-claiming, so the list includes
 * **pseudowords** — forms that obey the language's phonotactics but mean nothing. A
 * learner who claims pseudowords is over-claiming, and the estimate is corrected rather
 * than taken at face value.
 *
 * Why this exists at all: the closest competitor asks five onboarding questions and
 * then starts everyone at A1 regardless, which is its most-repeated pedagogical
 * complaint (docs/research/03-competitor-pingo.md §4). An onboarding step that asks a
 * learner something and then ignores the answer is worse than not asking.
 */

export interface ProbeWord {
  readonly id: string;
  readonly display: string;
  /**
   * Active-vocabulary size this word implies knowing, from its frequency band. A
   * learner who knows it probably knows most words commoner than it.
   */
  readonly impliesVocab: number;
  /** True for a form that looks native but is not a word. Never counted as knowledge. */
  readonly pseudo?: boolean;
}

/**
 * Spanish probe, banded across the frequency range the CEFR vocabulary anchors use.
 *
 * Deliberately short — 24 real words and 6 pseudowords takes under a minute, and the
 * estimate is held at low confidence anyway. A longer test would buy precision we throw
 * away as soon as there are real utterances to judge.
 */
const ES: readonly ProbeWord[] = [
  { id: 'es-p-001', display: 'casa', impliesVocab: 300 },
  { id: 'es-p-002', display: 'comer', impliesVocab: 300 },
  { id: 'es-p-003', display: 'grande', impliesVocab: 400 },
  { id: 'es-p-004', display: 'ayer', impliesVocab: 600 },
  { id: 'es-p-005', display: 'llegar', impliesVocab: 600 },
  { id: 'es-p-006', display: 'ventana', impliesVocab: 900 },
  { id: 'es-p-007', display: 'quizás', impliesVocab: 900 },
  { id: 'es-p-008', display: 'apagar', impliesVocab: 1200 },
  { id: 'es-p-009', display: 'orgulloso', impliesVocab: 1500 },
  { id: 'es-p-010', display: 'alcanzar', impliesVocab: 1500 },
  { id: 'es-p-011', display: 'desarrollo', impliesVocab: 2000 },
  { id: 'es-p-012', display: 'aprovechar', impliesVocab: 2500 },
  { id: 'es-p-013', display: 'plantear', impliesVocab: 3000 },
  { id: 'es-p-014', display: 'imprescindible', impliesVocab: 3000 },
  { id: 'es-p-015', display: 'acogedor', impliesVocab: 4000 },
  { id: 'es-p-016', display: 'reivindicar', impliesVocab: 5000 },
  { id: 'es-p-017', display: 'soslayar', impliesVocab: 6000 },
  { id: 'es-p-018', display: 'atisbar', impliesVocab: 6000 },
  { id: 'es-p-019', display: 'cuantioso', impliesVocab: 8000 },
  { id: 'es-p-020', display: 'zozobra', impliesVocab: 8000 },
  { id: 'es-p-021', display: 'ínclito', impliesVocab: 10000 },
  { id: 'es-p-022', display: 'denostar', impliesVocab: 10000 },
  { id: 'es-p-023', display: 'baladí', impliesVocab: 10000 },
  { id: 'es-p-024', display: 'ominoso', impliesVocab: 8000 },
  // Pseudowords: Spanish phonotactics, no meaning.
  { id: 'es-p-x01', display: 'traminar', impliesVocab: 0, pseudo: true },
  { id: 'es-p-x02', display: 'folquero', impliesVocab: 0, pseudo: true },
  { id: 'es-p-x03', display: 'lastrudo', impliesVocab: 0, pseudo: true },
  { id: 'es-p-x04', display: 'negrencia', impliesVocab: 0, pseudo: true },
  { id: 'es-p-x05', display: 'abrimiento', impliesVocab: 0, pseudo: true },
  { id: 'es-p-x06', display: 'quimbrar', impliesVocab: 0, pseudo: true },
];

/** French probe, banded on the same vocabulary anchors as the Spanish one. */
const FR: readonly ProbeWord[] = [
  { id: 'fr-p-001', display: 'maison', impliesVocab: 300 },
  { id: 'fr-p-002', display: 'manger', impliesVocab: 300 },
  { id: 'fr-p-003', display: 'grand', impliesVocab: 400 },
  { id: 'fr-p-004', display: 'hier', impliesVocab: 600 },
  { id: 'fr-p-005', display: 'arriver', impliesVocab: 600 },
  { id: 'fr-p-006', display: 'fenêtre', impliesVocab: 900 },
  { id: 'fr-p-007', display: 'peut-être', impliesVocab: 900 },
  { id: 'fr-p-008', display: 'éteindre', impliesVocab: 1200 },
  { id: 'fr-p-009', display: 'fier', impliesVocab: 1500 },
  { id: 'fr-p-010', display: 'atteindre', impliesVocab: 1500 },
  { id: 'fr-p-011', display: 'développement', impliesVocab: 2000 },
  { id: 'fr-p-012', display: 'profiter', impliesVocab: 2500 },
  { id: 'fr-p-013', display: 'soulever', impliesVocab: 3000 },
  { id: 'fr-p-014', display: 'incontournable', impliesVocab: 3000 },
  { id: 'fr-p-015', display: 'chaleureux', impliesVocab: 4000 },
  { id: 'fr-p-016', display: 'revendiquer', impliesVocab: 5000 },
  { id: 'fr-p-017', display: 'contourner', impliesVocab: 6000 },
  { id: 'fr-p-018', display: 'apercevoir', impliesVocab: 6000 },
  { id: 'fr-p-019', display: 'considérable', impliesVocab: 8000 },
  { id: 'fr-p-020', display: 'désarroi', impliesVocab: 8000 },
  { id: 'fr-p-021', display: 'illustre', impliesVocab: 10000 },
  { id: 'fr-p-022', display: 'dénigrer', impliesVocab: 10000 },
  { id: 'fr-p-023', display: 'futile', impliesVocab: 10000 },
  { id: 'fr-p-024', display: 'funeste', impliesVocab: 8000 },
  // Pseudowords: French phonotactics, no meaning.
  { id: 'fr-p-x01', display: 'tramigner', impliesVocab: 0, pseudo: true },
  { id: 'fr-p-x02', display: 'folquier', impliesVocab: 0, pseudo: true },
  { id: 'fr-p-x03', display: 'lastrude', impliesVocab: 0, pseudo: true },
  { id: 'fr-p-x04', display: 'négrence', impliesVocab: 0, pseudo: true },
  { id: 'fr-p-x05', display: 'ouvrissement', impliesVocab: 0, pseudo: true },
  { id: 'fr-p-x06', display: 'quimbrer', impliesVocab: 0, pseudo: true },
];

/** Portuguese probe, banded on the same vocabulary anchors as the Spanish one. */
const PT: readonly ProbeWord[] = [
  { id: 'pt-p-001', display: 'casa', impliesVocab: 300 },
  { id: 'pt-p-002', display: 'comer', impliesVocab: 300 },
  { id: 'pt-p-003', display: 'grande', impliesVocab: 400 },
  { id: 'pt-p-004', display: 'ontem', impliesVocab: 600 },
  { id: 'pt-p-005', display: 'chegar', impliesVocab: 600 },
  { id: 'pt-p-006', display: 'janela', impliesVocab: 900 },
  { id: 'pt-p-007', display: 'talvez', impliesVocab: 900 },
  { id: 'pt-p-008', display: 'apagar', impliesVocab: 1200 },
  { id: 'pt-p-009', display: 'orgulhoso', impliesVocab: 1500 },
  { id: 'pt-p-010', display: 'alcançar', impliesVocab: 1500 },
  { id: 'pt-p-011', display: 'desenvolvimento', impliesVocab: 2000 },
  { id: 'pt-p-012', display: 'aproveitar', impliesVocab: 2500 },
  { id: 'pt-p-013', display: 'levantar', impliesVocab: 3000 },
  { id: 'pt-p-014', display: 'imprescindível', impliesVocab: 3000 },
  { id: 'pt-p-015', display: 'acolhedor', impliesVocab: 4000 },
  { id: 'pt-p-016', display: 'reivindicar', impliesVocab: 5000 },
  { id: 'pt-p-017', display: 'contornar', impliesVocab: 6000 },
  { id: 'pt-p-018', display: 'avistar', impliesVocab: 6000 },
  { id: 'pt-p-019', display: 'considerável', impliesVocab: 8000 },
  { id: 'pt-p-020', display: 'desalento', impliesVocab: 8000 },
  { id: 'pt-p-021', display: 'ínclito', impliesVocab: 10000 },
  { id: 'pt-p-022', display: 'denegrir', impliesVocab: 10000 },
  { id: 'pt-p-023', display: 'fútil', impliesVocab: 10000 },
  { id: 'pt-p-024', display: 'funesto', impliesVocab: 8000 },
  // Pseudowords: Portuguese phonotactics, no meaning.
  { id: 'pt-p-x01', display: 'traminar', impliesVocab: 0, pseudo: true },
  { id: 'pt-p-x02', display: 'folqueiro', impliesVocab: 0, pseudo: true },
  { id: 'pt-p-x03', display: 'lastrudo', impliesVocab: 0, pseudo: true },
  { id: 'pt-p-x04', display: 'negrência', impliesVocab: 0, pseudo: true },
  { id: 'pt-p-x05', display: 'abrimento', impliesVocab: 0, pseudo: true },
  { id: 'pt-p-x06', display: 'quimbrar', impliesVocab: 0, pseudo: true },
];

/** German probe, banded on the same vocabulary anchors as the Spanish one. */
const DE: readonly ProbeWord[] = [
  { id: 'de-p-001', display: 'Haus', impliesVocab: 300 },
  { id: 'de-p-002', display: 'essen', impliesVocab: 300 },
  { id: 'de-p-003', display: 'groß', impliesVocab: 400 },
  { id: 'de-p-004', display: 'gestern', impliesVocab: 600 },
  { id: 'de-p-005', display: 'ankommen', impliesVocab: 600 },
  { id: 'de-p-006', display: 'Fenster', impliesVocab: 900 },
  { id: 'de-p-007', display: 'vielleicht', impliesVocab: 900 },
  { id: 'de-p-008', display: 'ausschalten', impliesVocab: 1200 },
  { id: 'de-p-009', display: 'stolz', impliesVocab: 1500 },
  { id: 'de-p-010', display: 'erreichen', impliesVocab: 1500 },
  { id: 'de-p-011', display: 'Entwicklung', impliesVocab: 2000 },
  { id: 'de-p-012', display: 'ausnutzen', impliesVocab: 2500 },
  { id: 'de-p-013', display: 'darlegen', impliesVocab: 3000 },
  { id: 'de-p-014', display: 'unverzichtbar', impliesVocab: 3000 },
  { id: 'de-p-015', display: 'gemütlich', impliesVocab: 4000 },
  { id: 'de-p-016', display: 'einfordern', impliesVocab: 5000 },
  { id: 'de-p-017', display: 'umgehen', impliesVocab: 6000 },
  { id: 'de-p-018', display: 'erblicken', impliesVocab: 6000 },
  { id: 'de-p-019', display: 'erheblich', impliesVocab: 8000 },
  { id: 'de-p-020', display: 'Verzweiflung', impliesVocab: 8000 },
  { id: 'de-p-021', display: 'erlaucht', impliesVocab: 10000 },
  { id: 'de-p-022', display: 'verunglimpfen', impliesVocab: 10000 },
  { id: 'de-p-023', display: 'belanglos', impliesVocab: 10000 },
  { id: 'de-p-024', display: 'verhängnisvoll', impliesVocab: 8000 },
  // Pseudowords: German phonotactics, no meaning.
  { id: 'de-p-x01', display: 'tramieren', impliesVocab: 0, pseudo: true },
  { id: 'de-p-x02', display: 'Folkert', impliesVocab: 0, pseudo: true },
  { id: 'de-p-x03', display: 'lastrudig', impliesVocab: 0, pseudo: true },
  { id: 'de-p-x04', display: 'Negrenz', impliesVocab: 0, pseudo: true },
  { id: 'de-p-x05', display: 'Öffnismus', impliesVocab: 0, pseudo: true },
  { id: 'de-p-x06', display: 'quimbern', impliesVocab: 0, pseudo: true },
];

/** Italian probe, banded on the same vocabulary anchors as the Spanish one. */
const IT: readonly ProbeWord[] = [
  { id: 'it-p-001', display: 'casa', impliesVocab: 300 },
  { id: 'it-p-002', display: 'mangiare', impliesVocab: 300 },
  { id: 'it-p-003', display: 'grande', impliesVocab: 400 },
  { id: 'it-p-004', display: 'ieri', impliesVocab: 600 },
  { id: 'it-p-005', display: 'arrivare', impliesVocab: 600 },
  { id: 'it-p-006', display: 'finestra', impliesVocab: 900 },
  { id: 'it-p-007', display: 'forse', impliesVocab: 900 },
  { id: 'it-p-008', display: 'spegnere', impliesVocab: 1200 },
  { id: 'it-p-009', display: 'orgoglioso', impliesVocab: 1500 },
  { id: 'it-p-010', display: 'raggiungere', impliesVocab: 1500 },
  { id: 'it-p-011', display: 'sviluppo', impliesVocab: 2000 },
  { id: 'it-p-012', display: 'approfittare', impliesVocab: 2500 },
  { id: 'it-p-013', display: 'sollevare', impliesVocab: 3000 },
  { id: 'it-p-014', display: 'imprescindibile', impliesVocab: 3000 },
  { id: 'it-p-015', display: 'accogliente', impliesVocab: 4000 },
  { id: 'it-p-016', display: 'rivendicare', impliesVocab: 5000 },
  { id: 'it-p-017', display: 'aggirare', impliesVocab: 6000 },
  { id: 'it-p-018', display: 'scorgere', impliesVocab: 6000 },
  { id: 'it-p-019', display: 'considerevole', impliesVocab: 8000 },
  { id: 'it-p-020', display: 'sgomento', impliesVocab: 8000 },
  { id: 'it-p-021', display: 'inclito', impliesVocab: 10000 },
  { id: 'it-p-022', display: 'denigrare', impliesVocab: 10000 },
  { id: 'it-p-023', display: 'futile', impliesVocab: 10000 },
  { id: 'it-p-024', display: 'funesto', impliesVocab: 8000 },
  // Pseudowords: Italian phonotactics, no meaning.
  { id: 'it-p-x01', display: 'traminare', impliesVocab: 0, pseudo: true },
  { id: 'it-p-x02', display: 'folchiero', impliesVocab: 0, pseudo: true },
  { id: 'it-p-x03', display: 'lastrudo', impliesVocab: 0, pseudo: true },
  { id: 'it-p-x04', display: 'negrenza', impliesVocab: 0, pseudo: true },
  { id: 'it-p-x05', display: 'aprimento', impliesVocab: 0, pseudo: true },
  { id: 'it-p-x06', display: 'quimbrare', impliesVocab: 0, pseudo: true },
];

/** Japanese probe, banded on the same vocabulary anchors as the Spanish one. */
const JA: readonly ProbeWord[] = [
  { id: 'ja-p-001', display: '家', impliesVocab: 300 },
  { id: 'ja-p-002', display: '食べる', impliesVocab: 300 },
  { id: 'ja-p-003', display: '大きい', impliesVocab: 400 },
  { id: 'ja-p-004', display: '昨日', impliesVocab: 600 },
  { id: 'ja-p-005', display: '着く', impliesVocab: 600 },
  { id: 'ja-p-006', display: '窓', impliesVocab: 900 },
  { id: 'ja-p-007', display: 'たぶん', impliesVocab: 900 },
  { id: 'ja-p-008', display: '消す', impliesVocab: 1200 },
  { id: 'ja-p-009', display: '誇らしい', impliesVocab: 1500 },
  { id: 'ja-p-010', display: '達する', impliesVocab: 1500 },
  { id: 'ja-p-011', display: '開発', impliesVocab: 2000 },
  { id: 'ja-p-012', display: '活用する', impliesVocab: 2500 },
  { id: 'ja-p-013', display: '提起する', impliesVocab: 3000 },
  { id: 'ja-p-014', display: '不可欠', impliesVocab: 3000 },
  { id: 'ja-p-015', display: '居心地', impliesVocab: 4000 },
  { id: 'ja-p-016', display: '主張する', impliesVocab: 5000 },
  { id: 'ja-p-017', display: '回避する', impliesVocab: 6000 },
  { id: 'ja-p-018', display: '垣間見る', impliesVocab: 6000 },
  { id: 'ja-p-019', display: '相当', impliesVocab: 8000 },
  { id: 'ja-p-020', display: '不安', impliesVocab: 8000 },
  { id: 'ja-p-021', display: '高名', impliesVocab: 10000 },
  { id: 'ja-p-022', display: '中傷する', impliesVocab: 10000 },
  { id: 'ja-p-023', display: '些細', impliesVocab: 10000 },
  { id: 'ja-p-024', display: '不吉', impliesVocab: 8000 },
  // Pseudowords: Japanese phonotactics, no meaning.
  { id: 'ja-p-x01', display: 'たらみん', impliesVocab: 0, pseudo: true },
  { id: 'ja-p-x02', display: 'ふるけい', impliesVocab: 0, pseudo: true },
  { id: 'ja-p-x03', display: 'らすとる', impliesVocab: 0, pseudo: true },
  { id: 'ja-p-x04', display: 'ねぐれん', impliesVocab: 0, pseudo: true },
  { id: 'ja-p-x05', display: 'あびめん', impliesVocab: 0, pseudo: true },
  { id: 'ja-p-x06', display: 'きんびる', impliesVocab: 0, pseudo: true },
];

/** Korean probe, banded on the same vocabulary anchors as the Spanish one. */
const KO: readonly ProbeWord[] = [
  { id: 'ko-p-001', display: '집', impliesVocab: 300 },
  { id: 'ko-p-002', display: '먹다', impliesVocab: 300 },
  { id: 'ko-p-003', display: '크다', impliesVocab: 400 },
  { id: 'ko-p-004', display: '어제', impliesVocab: 600 },
  { id: 'ko-p-005', display: '도착하다', impliesVocab: 600 },
  { id: 'ko-p-006', display: '창문', impliesVocab: 900 },
  { id: 'ko-p-007', display: '아마', impliesVocab: 900 },
  { id: 'ko-p-008', display: '끄다', impliesVocab: 1200 },
  { id: 'ko-p-009', display: '자랑스럽다', impliesVocab: 1500 },
  { id: 'ko-p-010', display: '도달하다', impliesVocab: 1500 },
  { id: 'ko-p-011', display: '개발', impliesVocab: 2000 },
  { id: 'ko-p-012', display: '활용하다', impliesVocab: 2500 },
  { id: 'ko-p-013', display: '제기하다', impliesVocab: 3000 },
  { id: 'ko-p-014', display: '필수적', impliesVocab: 3000 },
  { id: 'ko-p-015', display: '아늑하다', impliesVocab: 4000 },
  { id: 'ko-p-016', display: '주장하다', impliesVocab: 5000 },
  { id: 'ko-p-017', display: '우회하다', impliesVocab: 6000 },
  { id: 'ko-p-018', display: '엿보다', impliesVocab: 6000 },
  { id: 'ko-p-019', display: '상당하다', impliesVocab: 8000 },
  { id: 'ko-p-020', display: '불안', impliesVocab: 8000 },
  { id: 'ko-p-021', display: '저명하다', impliesVocab: 10000 },
  { id: 'ko-p-022', display: '비방하다', impliesVocab: 10000 },
  { id: 'ko-p-023', display: '사소하다', impliesVocab: 10000 },
  { id: 'ko-p-024', display: '불길하다', impliesVocab: 8000 },
  // Pseudowords: Korean phonotactics, no meaning.
  { id: 'ko-p-x01', display: '타라민', impliesVocab: 0, pseudo: true },
  { id: 'ko-p-x02', display: '폴케이', impliesVocab: 0, pseudo: true },
  { id: 'ko-p-x03', display: '라스트루', impliesVocab: 0, pseudo: true },
  { id: 'ko-p-x04', display: '네그렌', impliesVocab: 0, pseudo: true },
  { id: 'ko-p-x05', display: '아비멘', impliesVocab: 0, pseudo: true },
  { id: 'ko-p-x06', display: '킴브르', impliesVocab: 0, pseudo: true },
];

/** Chinese probe, banded on the same vocabulary anchors as the Spanish one. */
const ZH: readonly ProbeWord[] = [
  { id: 'zh-p-001', display: '家', impliesVocab: 300 },
  { id: 'zh-p-002', display: '吃', impliesVocab: 300 },
  { id: 'zh-p-003', display: '大', impliesVocab: 400 },
  { id: 'zh-p-004', display: '昨天', impliesVocab: 600 },
  { id: 'zh-p-005', display: '到达', impliesVocab: 600 },
  { id: 'zh-p-006', display: '窗户', impliesVocab: 900 },
  { id: 'zh-p-007', display: '也许', impliesVocab: 900 },
  { id: 'zh-p-008', display: '关掉', impliesVocab: 1200 },
  { id: 'zh-p-009', display: '自豪', impliesVocab: 1500 },
  { id: 'zh-p-010', display: '达到', impliesVocab: 1500 },
  { id: 'zh-p-011', display: '发展', impliesVocab: 2000 },
  { id: 'zh-p-012', display: '利用', impliesVocab: 2500 },
  { id: 'zh-p-013', display: '提出', impliesVocab: 3000 },
  { id: 'zh-p-014', display: '不可或缺', impliesVocab: 3000 },
  { id: 'zh-p-015', display: '温馨', impliesVocab: 4000 },
  { id: 'zh-p-016', display: '主张', impliesVocab: 5000 },
  { id: 'zh-p-017', display: '回避', impliesVocab: 6000 },
  { id: 'zh-p-018', display: '窥见', impliesVocab: 6000 },
  { id: 'zh-p-019', display: '相当', impliesVocab: 8000 },
  { id: 'zh-p-020', display: '沮丧', impliesVocab: 8000 },
  { id: 'zh-p-021', display: '著名', impliesVocab: 10000 },
  { id: 'zh-p-022', display: '诋毁', impliesVocab: 10000 },
  { id: 'zh-p-023', display: '琐碎', impliesVocab: 10000 },
  { id: 'zh-p-024', display: '不祥', impliesVocab: 8000 },
  // Pseudowords: Chinese phonotactics, no meaning.
  { id: 'zh-p-x01', display: '塔拉敏', impliesVocab: 0, pseudo: true },
  { id: 'zh-p-x02', display: '佛可耶', impliesVocab: 0, pseudo: true },
  { id: 'zh-p-x03', display: '拉斯图', impliesVocab: 0, pseudo: true },
  { id: 'zh-p-x04', display: '涅格伦', impliesVocab: 0, pseudo: true },
  { id: 'zh-p-x05', display: '阿比门', impliesVocab: 0, pseudo: true },
  { id: 'zh-p-x06', display: '钦布尔', impliesVocab: 0, pseudo: true },
];

/** English probe, banded on the same vocabulary anchors as the Spanish one. */
const EN: readonly ProbeWord[] = [
  { id: 'en-p-001', display: 'house', impliesVocab: 300 },
  { id: 'en-p-002', display: 'eat', impliesVocab: 300 },
  { id: 'en-p-003', display: 'big', impliesVocab: 400 },
  { id: 'en-p-004', display: 'yesterday', impliesVocab: 600 },
  { id: 'en-p-005', display: 'arrive', impliesVocab: 600 },
  { id: 'en-p-006', display: 'window', impliesVocab: 900 },
  { id: 'en-p-007', display: 'maybe', impliesVocab: 900 },
  { id: 'en-p-008', display: 'switch off', impliesVocab: 1200 },
  { id: 'en-p-009', display: 'proud', impliesVocab: 1500 },
  { id: 'en-p-010', display: 'reach', impliesVocab: 1500 },
  { id: 'en-p-011', display: 'development', impliesVocab: 2000 },
  { id: 'en-p-012', display: 'take advantage', impliesVocab: 2500 },
  { id: 'en-p-013', display: 'put forward', impliesVocab: 3000 },
  { id: 'en-p-014', display: 'indispensable', impliesVocab: 3000 },
  { id: 'en-p-015', display: 'cosy', impliesVocab: 4000 },
  { id: 'en-p-016', display: 'assert', impliesVocab: 5000 },
  { id: 'en-p-017', display: 'circumvent', impliesVocab: 6000 },
  { id: 'en-p-018', display: 'glimpse', impliesVocab: 6000 },
  { id: 'en-p-019', display: 'considerable', impliesVocab: 8000 },
  { id: 'en-p-020', display: 'dismay', impliesVocab: 8000 },
  { id: 'en-p-021', display: 'illustrious', impliesVocab: 10000 },
  { id: 'en-p-022', display: 'denigrate', impliesVocab: 10000 },
  { id: 'en-p-023', display: 'trifling', impliesVocab: 10000 },
  { id: 'en-p-024', display: 'baleful', impliesVocab: 8000 },
  // Pseudowords: English phonotactics, no meaning.
  { id: 'en-p-x01', display: 'tramine', impliesVocab: 0, pseudo: true },
  { id: 'en-p-x02', display: 'folquer', impliesVocab: 0, pseudo: true },
  { id: 'en-p-x03', display: 'lastrud', impliesVocab: 0, pseudo: true },
  { id: 'en-p-x04', display: 'negrence', impliesVocab: 0, pseudo: true },
  { id: 'en-p-x05', display: 'openment', impliesVocab: 0, pseudo: true },
  { id: 'en-p-x06', display: 'quimber', impliesVocab: 0, pseudo: true },
];

const PROBES: Readonly<Record<string, readonly ProbeWord[]>> = {
  fr: FR,
  pt: PT,
  de: DE,
  it: IT,
  ja: JA,
  ko: KO,
  zh: ZH,
  en: EN,
  es: ES,
};

/** The probe for a language, or an empty list when we have not built one yet. */
export function probeFor(language: string): readonly ProbeWord[] {
  return PROBES[language] ?? [];
}

export interface ProbeResult {
  /** Active-vocabulary estimate, already corrected for over-claiming. */
  readonly vocabEstimate: number;
  /** Fraction of pseudowords claimed, 0–1. */
  readonly falseAlarmRate: number;
  /**
   * True when the learner claimed enough pseudowords that the answers carry no
   * information. The UI says so rather than silently halving the number.
   */
  readonly overclaimed: boolean;
}

/**
 * Scores the probe.
 *
 * The correction is the standard hit-rate/false-alarm adjustment: claiming pseudowords
 * means claims in general are inflated, so the real-word hit rate is discounted in
 * proportion. At a 50% false-alarm rate the answers carry no information and the
 * estimate collapses, which is the honest outcome rather than a flattering one.
 */
export function scoreProbe(language: string, claimedIds: readonly string[]): ProbeResult {
  const words = probeFor(language);
  const claimed = new Set(claimedIds);

  const pseudo = words.filter((w) => w.pseudo === true);
  const real = words.filter((w) => w.pseudo !== true);

  const falseAlarms = pseudo.filter((w) => claimed.has(w.id)).length;
  const falseAlarmRate = pseudo.length === 0 ? 0 : falseAlarms / pseudo.length;

  const hits = real.filter((w) => claimed.has(w.id));
  const hitRate = real.length === 0 ? 0 : hits.length / real.length;
  const corrected = Math.max(0, (hitRate - falseAlarmRate) / Math.max(0.0001, 1 - falseAlarmRate));

  // The highest band the learner credibly reaches, scaled by the correction. Taking the
  // maximum rather than the mean matters: knowing one rare word is real evidence, and a
  // mean would wash it out against the common words everybody knows.
  const ceiling = hits.reduce((max, w) => Math.max(max, w.impliesVocab), 0);

  return {
    vocabEstimate: Math.round(ceiling * corrected),
    falseAlarmRate,
    overclaimed: falseAlarmRate >= 0.5,
  };
}

/**
 * The starting sub-level implied by a probe.
 *
 * Capped at B1.2 whatever the probe says: recognising a word is not being able to say
 * it, and starting someone above B1 on vocabulary evidence alone would drop them into
 * conversations they cannot hold. Real utterances lift the cap within a session or two.
 *
 * `vocabToIndex` and `sublevels` are passed in rather than imported so this module
 * stays free of a dependency on @saysooner/core, which depends on content.
 */
export function levelFromProbe<T extends string>(
  result: ProbeResult,
  vocabToIndex: (vocab: number) => number,
  sublevels: readonly [T, ...T[]],
): T {
  const floor = sublevels[0];
  if (result.overclaimed) return floor;

  const index = Math.round(vocabToIndex(result.vocabEstimate));
  const ceiling = sublevels.indexOf('B1.2' as T);
  const capped = ceiling === -1 ? index : Math.min(index, ceiling);
  return sublevels[Math.max(0, Math.min(capped, sublevels.length - 1))] ?? floor;
}
